import React, { useMemo } from 'react';

import { Image } from 'antd';

import styled from 'styled-components';

import { isEven, isOdd } from '@libs/NwUtils';

import NwIcon from '@components/Gui/NwIcon';
import { usePlaylistEditor, usePlaylistEditorDispatch } from '@components/PlaylistEditor/PlaylistEditorContext';
import { isPlaceholder } from '@components/PlaylistEditor/PlaylistEditorUtils';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableDivWrapper = styled.div`
    width: var(--item-width-playlist);
    height: auto;
    aspect-ratio: 3/4;
    background-color: ${(props) => props.theme.DarkerBorderColor};
    position: relative;
    font-size: 0.8rem;
    /* box-shadow:
        0px 2px 1px rgba(3, 7, 18, 0.08),
        0px 7px 4px rgba(3, 7, 18, 0.06),
        0px 16px 9px rgba(3, 7, 18, 0.05),
        0px 28px 15px rgba(3, 7, 18, 0.03),
        0px 44px 24px rgba(3, 7, 18, 0.02); */
    color: #fff;

    .ant-image {
        width: 100%;
        height: 100%;
        overflow: hidden;
        position: relative;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }
        .ant-image-mask {
            top: auto;
            left: auto;
            width: 100%;
            height: var(--item-preview-trigger-height);
            background-color: #000;
            opacity: 0.25;

            &:hover {
                opacity: 0.8;
                background-color: ${(props) => props.theme.PrimaryColor};
            }
        }
    }

    .sortable-video-preview {
        width: 100%;
        height: 100%;
        position: relative;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }

        .video-mask {
            position: absolute;
            top: auto;
            left: 0;
            bottom: 0;
            right: 0;

            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1rem;

            width: 100%;
            height: var(--item-preview-trigger-height);

            opacity: 0.25;
            color: #fff;
            background: rgba(0, 0, 0, 0.5);
            transition: opacity 0.3s;

            cursor: pointer;

            &:hover {
                opacity: 0.8;
                background-color: ${(props) => props.theme.PrimaryColor};
            }
        }
    }

    &.landscape {
        aspect-ratio: 16/10.4;
        width: calc((var(--item-width-playlist) * 2) + var(--playlist-page-inner-gap));
    }

    &.placeholder {
        background-color: #000;
        color: #fff;
    }

    &.margin-right {
        margin-right: var(--playlist-page-outer-gap);
    }

    &.single-page {
        margin-right: 1rem;

        &.landscape {
            width: calc((var(--item-width-playlist) * 2) + 1rem);
        }
    }

    &.dragging {
        box-shadow:
            0px 1px 1px rgba(3, 7, 18, 0.1),
            0px 5px 5px rgba(3, 7, 18, 0.08),
            0px 12px 12px rgba(3, 7, 18, 0.06),
            0px 20px 20px rgba(3, 7, 18, 0.04),
            0px 32px 32px rgba(3, 7, 18, 0.02);

        .sortable-remove-button {
            opacity: 0;
        }

        .ant-image {
            .ant-image-mask {
                opacity: 0;
            }
        }
    }

    .sortable-div-handler {
        background-color: transparent;
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: var(--item-preview-trigger-height);
        cursor: move;
        z-index: 100;
    }

    .sortable-remove-button {
        position: absolute;
        top: -0.5rem;
        right: -0.5rem;
        font-size: 1rem;
        background-color: ${(props) => props.theme.DangerColor};
        color: #fff;
        border-radius: 50%;
        width: 1.75rem;
        height: 1.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 200;
    }

    span {
        position: absolute;
        top: 0.5rem;
        left: 0.5rem;
    }
`;

const SortablePlaylistItem = ({ playlistItem, drag }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: `sortable-${playlistItem.ID}`,
    });

    const { singlePage } = usePlaylistEditor();
    const dispatch = usePlaylistEditorDispatch();

    let imageUrl = '';
    let thumbnailUrl = '';

    const ph = isPlaceholder(playlistItem);

    if (!ph) {
        const document = playlistItem.media;
        imageUrl = document.HD ? document.HD.Url : document.Standard.Url;
        thumbnailUrl = document.Thumbnail.Url;
    }

    // const style = transform
    //     ? {
    //           transform: CSS.Translate.toString(transform),
    //       }
    //     : undefined;

    const style = {
        position: 'relative',
        touchAction: 'none',
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition: transition,
    };

    const isPictureLandscape = !ph && playlistItem.isLandscape;

    const isVideo = !ph && playlistItem.media.Type === 'Video';

    const sortableDivClasses = useMemo(() => {
        let classes = 'playlist-sortable-item';
        if (ph) {
            classes += ' placeholder';
        }
        if (isPictureLandscape) {
            classes += ' landscape';
        }
        if (!singlePage && isEven(playlistItem.page)) {
            classes += ' margin-right';
        }
        if (drag || isDragging) {
            classes += ' dragging';
        }
        if (singlePage) {
            classes += ' single-page';
        }
        return classes;
    }, [ph, isPictureLandscape, playlistItem.page, drag, isDragging, singlePage]);

    const handleRemoveItem = () => {
        dispatch({
            type: 'RemovePlaylistItem',
            value: playlistItem.ID,
        });
    };

    const showRemoveButton = useMemo(() => {
        return !playlistItem.unremovable && !drag && !isDragging;
    }, [playlistItem.unremovable, drag, isDragging]);

    return (
        <SortableDivWrapper key={playlistItem.ID} className={sortableDivClasses} ref={setNodeRef} style={style}>
            <div className="sortable-div-handler" {...listeners} {...attributes}>
                &nbsp;
            </div>
            {!ph &&
                (isVideo ? (
                    <div
                        onClick={() =>
                            dispatch({
                                type: 'SetPreviewVideo',
                                value: playlistItem.media,
                            })
                        }
                        className="sortable-video-preview"
                    >
                        <img src={thumbnailUrl} />
                        <div className="video-mask">
                            <NwIcon icon={byPrefixAndName.fass['play']} />
                            Preview
                        </div>
                    </div>
                ) : (
                    <Image
                        src={thumbnailUrl}
                        preview={{
                            src: imageUrl,
                        }}
                    />
                ))}
            {showRemoveButton && (
                <div className="sortable-remove-button" onClick={handleRemoveItem}>
                    <NwIcon icon={byPrefixAndName.fass['times']} />
                </div>
            )}
        </SortableDivWrapper>
    );
};

export default SortablePlaylistItem;
